import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { PaperPlaneTilt } from "@phosphor-icons/react";

export function SendEmailModal({ open, onOpenChange, selectedCaptures, userEmail, onSent }) {
  const { t } = useTranslation();
  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/email/send", {
        capture_ids: selectedCaptures.map((c) => c.id),
        recipient_email: recipient,
        subject,
        message,
      });
      toast.success(t("email.sentSuccess", { recipient, cc: userEmail }));
      onSent();
      onOpenChange(false);
      setRecipient("");
      setSubject("");
      setMessage("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg" data-testid="send-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{t("email.titleWithCount", { count: selectedCaptures.length })}</DialogTitle>
          <DialogDescription>{t("email.description")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="recipient">{t("email.recipientLabel")}</Label>
            <Input id="recipient" data-testid="send-email-recipient-input" type="email" required value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder={t("email.recipientPlaceholder")} className="rounded-none" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="subject">{t("email.subjectLabel")}</Label>
            <Input id="subject" data-testid="send-email-subject-input" required value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t("email.subjectPlaceholder")} className="rounded-none" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="message">{t("email.messageLabel")}</Label>
            <Textarea id="message" data-testid="send-email-message-input" value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("email.messagePlaceholder")} className="rounded-none" />
          </div>
          <p className="text-xs text-muted-foreground font-mono-tag">{t("email.ccHint", { email: userEmail })}</p>
          <Button type="submit" disabled={loading} data-testid="send-email-submit-button" className="w-full rounded-none gap-2">
            <PaperPlaneTilt size={16} weight="bold" /> {loading ? t("email.sending") : t("email.send")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
